import React from 'react';
import { Database, Download, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Switch } from '../../../../components/ui/switch';
import type { SystemSettings } from '../../types';

interface BackupSettingsProps {
  settings: SystemSettings['backup'];
  onSettingChange: (field: keyof SystemSettings['backup'], value: boolean | string | number) => void;
  onBackupNow: () => void;
}

export const BackupSettings: React.FC<BackupSettingsProps> = ({
  settings,
  onSettingChange,
  onBackupNow
}) => {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Database className="h-5 w-5 text-[#e04530]" />
        Backup & Maintenance
      </h2>
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-6 space-y-4">
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">Automatic Backups</h3>
                <p className="text-sm text-gray-500">Enable scheduled backups</p>
              </div>
              <Switch 
                checked={settings.autoBackup}
                onCheckedChange={(value: boolean) => onSettingChange('autoBackup', value)}
              />
            </div>

            {settings.autoBackup && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Backup Frequency</h3>
                    <p className="text-sm text-gray-500">How often to create backups</p>
                  </div>
                  <Select 
                    value={settings.backupFrequency}
                    onValueChange={(value) => onSettingChange('backupFrequency', value)}
                  >
                    <SelectTrigger className="w-[180px] bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Retention Period</h3>
                    <p className="text-sm text-gray-500">Days to keep backups</p>
                  </div>
                  <Select 
                    value={settings.retentionDays.toString()}
                    onValueChange={(value) => onSettingChange('retentionDays', parseInt(value, 10))}
                  >
                    <SelectTrigger className="w-[180px] bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex items-center gap-4 pt-4 border-t">
              <Button
                onClick={onBackupNow}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Backup Now
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Restore Backup
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 